import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { votingApi } from '../services/api';
import { VotingSession, User } from '../types';
import { VotingHistoryItem } from '../types/history';
import { getRoomHistory, clearVotingHistory, getHistoryStatistics } from '../utils/votingHistory';
import { downloadJson, downloadCsv, historyFilename } from '../utils/download';
import { averageVote } from '../utils/voteStats';
import LocalHistoryList from './history/LocalHistoryList';
import ServerHistoryList from './history/ServerHistoryList';
import './HistoryPanel.css';

interface HistoryPanelProps {
  roomId: string;
  participants: User[];
}

type HistoryTab = 'local' | 'server';

const HistoryPanel: React.FC<HistoryPanelProps> = ({ roomId, participants }) => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<VotingSession[]>([]);
  const [localHistory, setLocalHistory] = useState<VotingHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<HistoryTab>('local'); // localStorageを優先表示

  const loadSessionHistory = useCallback(async () => {
    try {
      setLoading(true);
      setLocalHistory(getRoomHistory(roomId));

      // サーバー履歴の取得失敗はローカル履歴で代替できるため致命的エラーにしない
      try {
        setSessions(await votingApi.getSessionHistory(roomId));
      } catch (serverErr) {
        console.log('Server history not available:', serverErr);
      }
    } catch (err) {
      console.error('Failed to load history:', err);
      setError(t('errors.genericError') || '履歴の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
    // tを含めることで言語切替時にエラーメッセージも再取得・再翻訳される
  }, [roomId, t]);

  useEffect(() => {
    loadSessionHistory();
  }, [loadSessionHistory]);

  // タブを切り替えたら展開状態をリセットする（別タブのセッションIDが偶然一致して開いたままになるのを防ぐ）
  const switchTab = (tab: HistoryTab) => {
    setActiveTab(tab);
    setExpandedSession(null);
  };

  const toggleExpanded = (id: string) => {
    setExpandedSession((current) => (current === id ? null : id));
  };

  const handleClearHistory = () => {
    if (window.confirm(t('history.actions.confirmClear') || 'ローカルに保存された全ての履歴を削除しますか？この操作は取り消せません。')) {
      clearVotingHistory();
      setLocalHistory([]);
    }
  };

  const handleExportLocal = (format: 'json' | 'csv') => {
    if (format === 'json') {
      downloadJson(historyFilename(roomId, 'json'), localHistory);
      return;
    }

    downloadCsv(
      historyFilename(roomId, 'csv'),
      ['Story Title', 'Final Point', 'Average Point', 'Participants', 'Voted At'],
      localHistory.map((item) => [
        item.storyTitle,
        item.finalPoint,
        item.averagePoint ?? 'N/A',
        item.participants.join(', '),
        item.votedAt,
      ])
    );
  };

  const nameFor = (userId: string) =>
    participants.find((p) => p.id === userId)?.display_name || userId;

  const handleExportSessions = (format: 'json' | 'csv') => {
    if (format === 'json') {
      downloadJson(historyFilename(`${roomId}-sessions`, 'json'), sessions);
      return;
    }

    downloadCsv(
      historyFilename(`${roomId}-sessions`, 'csv'),
      ['Story Title', 'Description', 'Average', 'Votes', 'Created At', 'Finished At'],
      sessions.map((session) => {
        const votes = Object.values(session.votes || {});
        const average = averageVote(votes);
        return [
          session.story_title,
          session.story_description || '',
          average === undefined ? 'N/A' : average.toFixed(1),
          votes.map((v: any) => `${nameFor(v.user_id)}:${v.value}`).join(' / '),
          session.created_at,
          session.finished_at || '',
        ];
      })
    );
  };

  if (loading) {
    return <div className="history-loading">{t('room.status.connecting') || '読み込み中...'}...</div>;
  }

  if (error) {
    return <div className="history-error">{error}</div>;
  }

  const stats = getHistoryStatistics();

  return (
    <div className="history-panel">
      <div className="history-header">
        <h3>{t('history.title') || '投票履歴'}</h3>
        <div className="history-tabs">
          <button
            className={`tab-button ${activeTab === 'local' ? 'active' : ''}`}
            onClick={() => switchTab('local')}
          >
            {t('history.localHistory') || 'ローカル履歴'} ({localHistory.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'server' ? 'active' : ''}`}
            onClick={() => switchTab('server')}
          >
            {t('history.sessionHistory') || 'セッション履歴'} ({sessions.length})
          </button>
        </div>
      </div>

      {activeTab === 'local' && localHistory.length > 0 && (
        <div className="history-stats">
          <div className="stat-item">
            <span className="stat-label">{t('history.statistics.totalSessions') || '総セッション数'}:</span>
            <span className="stat-value">{stats.totalSessions}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">{t('history.statistics.totalRooms') || '総ルーム数'}:</span>
            <span className="stat-value">{stats.totalRooms}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">{t('history.statistics.averagePoints') || '平均ポイント'}:</span>
            <span className="stat-value">{stats.averagePoints}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">{t('history.statistics.mostUsedPoint') || '最頻ポイント'}:</span>
            <span className="stat-value">{stats.mostUsedPoint}</span>
          </div>
        </div>
      )}

      <div className="export-buttons">
        {activeTab === 'local' ? (
          <>
            <button onClick={() => handleExportLocal('json')} className="export-button json" disabled={localHistory.length === 0}>
              {t('history.actions.exportJson') || 'JSON出力'}
            </button>
            <button onClick={() => handleExportLocal('csv')} className="export-button csv" disabled={localHistory.length === 0}>
              {t('history.actions.exportCsv') || 'CSV出力'}
            </button>
            <button onClick={handleClearHistory} className="export-button clear" disabled={localHistory.length === 0}>
              {t('history.actions.clearHistory') || '履歴削除'}
            </button>
          </>
        ) : (
          <>
            <button onClick={() => handleExportSessions('json')} className="export-button json">
              {t('history.actions.exportJson') || 'JSON出力'}
            </button>
            <button onClick={() => handleExportSessions('csv')} className="export-button csv">
              {t('history.actions.exportCsv') || 'CSV出力'}
            </button>
          </>
        )}
      </div>

      {activeTab === 'local' ? (
        <LocalHistoryList items={localHistory} expandedId={expandedSession} onToggle={toggleExpanded} />
      ) : (
        <ServerHistoryList
          sessions={sessions}
          participants={participants}
          expandedId={expandedSession}
          onToggle={toggleExpanded}
        />
      )}
    </div>
  );
};

export default HistoryPanel;
